<template>
  <div class="reservation-time-space">
    <BobPageTitle :config="pageConfig" />
    <FiltersContainer>
      <!-- <ServicesUnitSelect :model.sync="unit" :showAll="true" @change="refresh"/> -->
      <el-date-picker
        v-model="searchDate"
        editable
        :clearable="false"
        format="yyyy-MM-dd"
        :placeholder="$t('reservationTimeSpace.search.date.placeholder')"
        @change="onDateChange"
      />
    </FiltersContainer>
    <p v-if="reservationConfig.timeUnit" class="text-sub">
      {{ $t('reservationTimeSpace.hint.timeUnitEstimate', { timeUnit: reservationConfig.timeUnit }) }}
    </p>
    <div class="flex gap-[12px] items-start w-full overflow-x-auto">
      <section v-if="times" class="flex-1 min-w-0">
        <BaseTable
          v-if="salesUnitList.length === 0"
          class="test"
          :data="salesUnitList"
          :empty-text="$t('common.table.empty.text')"
        >
          <EmptyBlock slot="empty" />
          <BaseElTableColumn :label="$t('reservationTimeSpace.table.serviceUnit.title')" prop="name" fixed="left" />
        </BaseTable>

        <BaseTable
          v-if="salesUnitList.length > 0"
          v-loading="loadingControl.table"
          doLayout
          :data="salesUnitList"
          :empty-text="$t('common.table.empty.text')"
        >
          <BaseElTableColumn
            :label="$t('reservationTimeSpace.table.serviceUnit.title')"
            width="120px"
            align="center"
            prop="name"
            fixed="left"
          />
          <BaseElTableColumn
            v-for="num in filteredTimes"
            :key="num"
            align="center"
            min-width="120px"
            :label="num"
            prop="availableTimes.times"
          >
            <template slot-scope="scope">
              <AvailableTimeCell
                :label="trans(periodBlock(scope))"
                :status="periodBlock(scope).status"
                :status-class-map="statusClassMap"
                :availableVolume="periodBlock(scope).availableVolume"
                :ariaLabel="`${trans(periodBlock(scope))}(${periodBlock(scope).availableVolume})`"
                :dataTestid="`${trans(periodBlock(scope))}(${periodBlock(scope).availableVolume})`"
                @click="onClickTimeCell(scope)"
              />
            </template>
          </BaseElTableColumn>
        </BaseTable>
        <Pagination
          :curPage.sync="tableOptions.page"
          :pageLimit="tableOptions.pageLimit"
          :total="salesUnitCount"
          @pageChange="refresh"
        />
      </section>

      <div v-if="dialog.create" v-loading="loadingControl.createReservationArea" class="create-reservation-wrapper">
        <div style="display: flex; flex-direction: column; gap: 20px">
          <p class="font-medium text-primary-100 text-lg">{{ $t('newCreateAppointment.title') }}</p>

          <div class="section-block">
            <div style="display: flex; flex-direction: column; gap: 10px">
              <p><span class="text-gray-80">選擇服務人員：</span>{{ select.unit ? select.unit.name : '-' }}</p>
              <p><span class="text-gray-80">選擇預約日期：</span>{{ select.start ? formatDate(select.start, 'YYYY/MM/DD') : '-' }}</p>
              <p><span class="text-gray-80">選擇預約時段：</span>{{ select.period ? select.period : '-' }}</p>
            </div>
          </div>

          <NewCreateAppointment
            ref="createReservationBlockRef"
            embedMode
            :preset="appointmentPreset"
            @close="handleCloseCreateReservation"
            @created="refresh"
          />
        </div>
      </div>
    </div>

    <!-- 關閉時段 Dialog -->
    <TogglePeriodDialog v-if="dialog.togglePeriod" :title="$t('shiftCalendar.dialog.editClosePeriod.title')" width="440px" @close="dialog.togglePeriod = false" @updated="refresh" />

    <AIGenerateTimeSlotAnnouncementModal
      v-if="dialog.aiGenerateAnnouncement"
      @close="dialog.aiGenerateAnnouncement = false"
    />

    <!-- 預約新增 Dialog -->
  </div>
</template>

<script>
import { GetServiceUnit, GetServiceUnitCount } from '@/api/serviceUnit'
import { FindReservationParameter } from '@/api/reservation'
import { GetPeriods } from '@/api/togglePeriod'
import { pageStartIndex as startIndex } from '@/utils/table'
import EmptyBlock from '@/components/EmptyBlock.vue'
import TogglePeriodDialog from '@/components/Dialog/TogglePeriodDialog.vue'
import BobPageTitle from '@/components/BobPageTitle.vue'
import NewCreateAppointment from '@/views/NewCreateAppointment/NewCreateAppointment.vue'
import { formatDate, getDay } from '@/utils/date'
import { ref, reactive, nextTick, computed, onMounted } from 'vue'
import { useShop } from '@/use/shop'
import AvailableTimeCell from '@/components/Reservation/AvailableTimeCell/AvailableTimeCell.vue'
import { useAvailableTimeSpace } from '@/use/useAvailableTimeSpace'
import AIGenerateTimeSlotAnnouncementModal from '@/components/AIGenerateTimeSlotAnnouncementModal.vue'
import { i18n } from '@/plugins/i18n/i18n'
import { useRouter } from 'vue-router/composables'
import { usePermissions } from '@/use/permissions'

export default {
  name: 'ReservationTimeSpace',
  components: {
    EmptyBlock,
    TogglePeriodDialog,
    NewCreateAppointment,
    AvailableTimeCell,
    BobPageTitle,
    AIGenerateTimeSlotAnnouncementModal,
  },
  setup () {
    const { shopId } = useShop()
    const router = useRouter()
    const {
      times,
      show,
      filteredTimes,
      genTimes,
      attachAvailableTimes,
      periodBlock,
      trans,
      makeOnClickTimeCell,
    } = useAvailableTimeSpace()
    const { checkAction } = usePermissions()

    // data
    const searchDate = ref(new Date())
    const unit = ref('')
    const loadingControl = reactive({
      createReservationArea: false,
      table: false,
    })
    const unitTimesList = ref([])
    const salesUnitList = ref([])
    const salesUnitCount = ref(0)
    const tableOptions = reactive({
      page: 1,
      pageLimit: 10,
    })

    const reservationConfig = reactive({})
    const dialog = reactive({ create: false, togglePeriod: false, aiGenerateAnnouncement: false })
    const select = reactive({ start: null, period: null, unit: null })

    const createReservationBlockRef = ref(null)

    // computed
    const pageStartIndex = computed(() => startIndex(tableOptions.page, tableOptions.pageLimit))
    const pageConfig = computed(() => {
      const config = {
        title: i18n.t('reservationTimeSpace.title'),
        titleSize: 20,
        actions: [
          {
            key: 'create',
            component: () => import('@/components/BaseElButton.vue'),
            render: () => i18n.t('newCreateAppointment.title'),
            props: { type: 'primary' },
            on: {
              click: onCreateReservation,
            },
            featureKey: 'admin.appointmentOrder.create',
          },
          {
            // AI 生成預約空檔公告
            key: 'aiGenerate',
            component: () => import('@/components/BaseElButton.vue'),
            render: () => 'AI 生成預約空檔公告',
            props: { type: 'primary' },
            layout: {
              align: 'center',
              gap: 4,
            },
            on: {
              click: () => {
                dialog.aiGenerateAnnouncement = true
              },
            },
            childs: [
              {
                key: 'icon',
                props: { size: 14 },
                component: () => import('@/components/MaterialIcon.vue'),
                render: () => 'auto_awesome',
              },
            ],
          },
          {
            key: 'closePeriod',
            component: () => import('@/components/BaseElButton.vue'),
            render: () => i18n.t('shiftCalendar.button.closePeriod.text'),
            props: { plain: true },
            on: {
              click: () => { dialog.togglePeriod = true },
            },
            featureKey: 'admin.appointmentScheduleBan.updateTimes',
          },
        ],
      }

      if (!checkAction('admin.appointmentTimeSlot.llmGenerateSlotText')) {
        config.actions = config.actions.filter(action => action.key !== 'aiGenerate')
      }

      // 過濾沒有權限的 action
      config.actions = config.actions.filter(action => {
        if (!action.featureKey) return true
        return checkAction(action.featureKey)
      })

      return config
    })

    const appointmentPreset = computed(() => {
      return {
        serviceUnit: select.unit,
        date: select.start,
        period: select.period,
        serviceType: 'humanService',
      }
    })

    const statusClassMap = computed(() => ({
      empty: 'text-gray-600 bg-[lightgray]',
      available: 'text-gray bg-[rgb(189,233,122)] hover:brightness-95',
      booking: 'text-white bg-[rgb(112,192,216)]',
      ban: 'text-white bg-[rgb(253,102,139)]',
    }))

    // methods
    const onDateChange = async () => {
      // 若右側面板已開啟，清空表單並帶入新日期
      if (dialog.create) {
        select.unit = null
        select.period = null
        select.start = new Date(searchDate.value)
        await nextTick()
        if (createReservationBlockRef.value && createReservationBlockRef.value.onReset) {
          createReservationBlockRef.value.onReset()
        }
      }
      refresh()
    }

    const refresh = async () => {
      loadingControl.table = true
      try {
        if (dialog.create) {
          loadingControl.createReservationArea = true
          select.start = new Date(searchDate.value)
          select.period = null
          nextTick(() => {
            if (createReservationBlockRef.value && createReservationBlockRef.value.onReset) {
              createReservationBlockRef.value.onReset()
            }
          })
          setTimeout(() => { loadingControl.createReservationArea = false }, 300)
        }
        await Promise.all([
          findReservationParameter(),
          getServiceUnit().then(() => findServiceUnit()),
          getServiceUnitCount(),
        ])
      } catch (error) {
        console.error(error)
        window.$message.error(error?.message || error)
      } finally {
        loadingControl.table = false
      }
    }

    const getServiceUnit = async () => {
      try {
        const res = await GetServiceUnit({
          shopId: shopId.value,
          start: pageStartIndex.value,
          limit: tableOptions.pageLimit,
        })
        // 確保每個 unit 都有響應式的 availableTimes 屬性
        salesUnitList.value = res.map(unit => ({
          ...unit,
          availableTimes: [],
          availableTimes2: { times: [] },
        }))
      } catch (error) {
        console.log(error)
        window.$message.error({
          message: error || error.message,
        })
      }
    }

    const getServiceUnitCount = async () => {
      try {
        salesUnitCount.value = await GetServiceUnitCount({ shopId: shopId.value })
      } catch (error) {
        console.log(error)
        window.$message.error({
          message: error || error.message,
        })
      }
    }

    const findServiceUnit = async () => {
      if (!salesUnitList.value.length) return

      try {
        const { start, end } = getDay(searchDate.value)
        // 創建批量請求獲取所有單元的數據
        const unitIds = salesUnitList.value.map(unit => unit.id)

        // 假設API支持批量查詢，否則需要修改API
        const results = await Promise.all(
          unitIds.map(id =>
            GetPeriods({
              shopId: shopId.value,
              appointmentUnitId: id,
              start,
              end,
            }),
          ),
        )

        // 使用 composable 的 attachAvailableTimes 處理結果
        attachAvailableTimes({
          items: salesUnitList.value,
          results,
          getResultForItem: (_item, idx, arr) => arr[idx],
          showRef: show,
        })
      } catch (error) {
        console.error(error)
      }
    }

    const findReservationParameter = async () => {
      try {
        const res = await FindReservationParameter({
          shopId: shopId.value,
        })
        Object.assign(reservationConfig, res)
      } catch (error) {
        console.log(error)
        window.$message.error(error)
      }
    }

    const onCreateReservation = () => {
      if (dialog.create) return
      select.start = new Date(searchDate.value)
      select.period = null
      select.unit = null
      dialog.create = true
    }

    const handleCloseCreateReservation = () => {
      dialog.create = false
      select.start = null
      select.period = null
      select.unit = null
    }

    const _onClickTimeCell = makeOnClickTimeCell({
      periodBlockFn: periodBlock,
      searchDateRef: searchDate,
      dialog,
      select,
      createReservationBlockRef,
      loadingControl,
      messages: {
        notAvailable: '此時段未排班，無法新增預約，請至排班行事曆進行排班',
        past: '已過當前時間，欲新增此時段預約，請點選右上角新增預約並使用加班預約',
      },
      setSelection: ({ scope, select, searchDate }) => {
        unit.value && (unit.value = '')
        select.unit = scope.row
        select.start = new Date(searchDate.value)
        select.period = scope.column.label
      },
      onPanelReset: ({ createReservationBlockRef }) => {
        if (createReservationBlockRef.value && createReservationBlockRef.value.onReset) {
          createReservationBlockRef.value.onReset()
        }
        const wrapper = createReservationBlockRef.value && createReservationBlockRef.value.$el && createReservationBlockRef.value.$el.parentElement
        if (wrapper && wrapper.scrollTo) {
          wrapper.scrollTo({ top: 0, behavior: 'smooth' })
        }
      },
    })

    // 包裝原始 onClick handler，加上權限檢查
    const onClickTimeCell = (scope) => {
      if (!checkAction('admin.appointmentOrder.create')) {
        return
      }

      // 有權限則呼叫原 handler
      return _onClickTimeCell(scope)
    }

    onMounted(async () => {
      await refresh()
      genTimes(reservationConfig)
    })

    return {
      pageConfig,
      searchDate,
      show,
      unit,
      times,
      loadingControl,
      unitTimesList,
      salesUnitList,
      salesUnitCount,
      tableOptions,
      reservationConfig,
      dialog,
      select,
      periodBlock,
      onCreateReservation,
      handleCloseCreateReservation,
      onClickTimeCell,
      formatDate,
      createReservationBlockRef,
      shopId,
      pageStartIndex,
      filteredTimes,
      appointmentPreset,
      refresh,
      genTimes,
      findReservationParameter,
      findServiceUnit,
      getServiceUnit,
      getServiceUnitCount,
      trans,
      onDateChange,
      statusClassMap,
    }
  },
}
</script>

<style scoped lang="postcss">
.create-reservation-wrapper {
  @apply border border-primary-100 p-[12px] rounded-[10px] flex-shrink-0 flex-1 max-w-[650px] shadow-md;
}
</style>
